import React, { useEffect, useMemo, useState } from 'react'
import { api } from '../../lib/api'
import { MatchBandBadge } from '../../components/ui'
import type { AdminMatchRow, AdminPatientRow, AdminProviderRow } from '../../lib/types'

export default function AdminMatching() {
  const [matches, setMatches] = useState<AdminMatchRow[]>([])
  const [patients, setPatients] = useState<AdminPatientRow[]>([])
  const [providers, setProviders] = useState<AdminProviderRow[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    Promise.all([
      api('/admin/matches?limit=300'),
      api('/admin/patients?limit=200'),
      api('/admin/providers?limit=200'),
    ]).then(([m, p, pr]) => {
      setMatches(m); setPatients(p); setProviders(pr); setLoading(false)
    })
  }, [])

  const patientName = (id: string) => patients.find((p) => p.id === id)?.name || `patient ${id.slice(0, 8)}`
  const providerName = (id: string) => providers.find((p) => p.id === id)?.name || `provider ${id.slice(0, 8)}`

  const searches = useMemo(() => {
    const groups = new Map<string, AdminMatchRow[]>()
    for (const m of matches) {
      const key = m.search_id || m.id
      if (!groups.has(key)) groups.set(key, [])
      groups.get(key)!.push(m)
    }
    return Array.from(groups.entries()).map(([searchId, rows]) => ({
      searchId,
      patientId: rows[0].patient_id,
      qualifiedReturned: rows.length,
      selected: rows.find((r) => r.selected),
      rows,
      createdAt: rows[0].created_at,
    })).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
  }, [matches])

  const rerunCounts = useMemo(() => {
    const byPatient = new Map<string, Set<string>>()
    for (const m of matches) {
      if (!m.search_id) continue
      if (!byPatient.has(m.patient_id)) byPatient.set(m.patient_id, new Set())
      byPatient.get(m.patient_id)!.add(m.search_id)
    }
    return byPatient
  }, [matches])

  if (loading) return <div className="page-loading">Loading matching activity…</div>

  return (
    <div>
      <div className="page-head">
        <h1>Matching activity</h1>
        <div className="ph-sub">Search runs, qualified providers returned, and selection outcomes.</div>
      </div>

      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th>Search / case ID</th><th>Care seeker</th><th>Qualified providers</th>
              <th>Selected provider</th><th>Match explanations</th><th>Re-runs for this patient</th><th>Run at</th>
            </tr>
          </thead>
          <tbody>
            {searches.map((s) => (
              <tr key={s.searchId}>
                <td title={s.searchId}>{s.searchId.slice(0, 8)}</td>
                <td>{patientName(s.patientId)}</td>
                <td>{s.qualifiedReturned}</td>
                <td>
                  {s.selected ? (
                    <>
                      {providerName(s.selected.provider_id)} <MatchBandBadge band={s.selected.match_band} />
                    </>
                  ) : <span className="muted">Not yet selected</span>}
                </td>
                <td className="muted" style={{ maxWidth: 260 }}>
                  {s.rows[0].match_reasons.slice(0, 2).join('; ') || '—'}
                </td>
                <td>{(rerunCounts.get(s.patientId)?.size || 1) > 1 ? `${rerunCounts.get(s.patientId)!.size} runs` : '1 run'}</td>
                <td className="muted">{new Date(s.createdAt).toLocaleString()}</td>
              </tr>
            ))}
            {searches.length === 0 && <tr><td colSpan={7} className="muted">No matching activity recorded yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  )
}
